//
// Copyright (C) 2013 - present Instructure, Inc.
//
// This file is part of Canvas.
//
// Canvas is free software: you can redistribute it and/or modify it under
// the terms of the GNU Affero General Public License as published by the Free
// Software Foundation, version 3 of the License.
//
// Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
// WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
// A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
// details.
//
// You should have received a copy of the GNU Affero General Public License along
// with this program. If not, see <http://www.gnu.org/licenses/>.

import $ from 'jquery'
import {View} from '@canvas/backbone'
import CollectionView from '@canvas/backbone-collection-view'
import GroupCategoryView from './GroupCategoryView'
import GroupCategory from '@canvas/groups/backbone/models/GroupCategory'
import groupCategoriesTemplate from '../../jst/groupCategories.handlebars'
import tabTemplate from '../../jst/groupCategoryTab.handlebars'
import awaitElement from '@canvas/await-element'
import {renderCreateDialog} from '@canvas/groups/react/CreateOrEditSetModal'
import 'jqueryui/tabs'
import {initializeTopNavPortalWithDefaults} from '@canvas/top-navigation/react/TopNavPortalWithDefaults'
import {useScope as createI18nScope} from '@canvas/i18n'
import {setupTabKeyboardNavigation} from '@canvas/util/tabKeyboardNavigation'

const I18n = createI18nScope('GroupCategoriesView')
export default class GroupCategoriesView extends CollectionView {
  static initClass() {
    this.prototype.template = groupCategoriesTemplate

    this.prototype.className = 'group_categories_area'

    this.prototype.els = {
      ...CollectionView.prototype.els,
      '#group_categories_tabs': '$tabs',
      'li.static': '$static',
      '#add-group-set': '$addGroupSetButton',
      '.empty-groupset-instructions': '$emptyInstructions',
    }

    // @ts-expect-error - Backbone View property
    this.prototype.events = {
      'click #add-group-set': 'addGroupSet',
      'tabsactivate #group_categories_tabs': 'activatedTab',
    }

    // @ts-expect-error - Backbone View property
    this.prototype.itemView = View.extend({
      tagName: 'li',
      template() {
        return tabTemplate({
          ...this.model.present(),
          id: this.model.id != null ? this.model.id : this.model.cid,
        })
      },
    })
    // @ts-expect-error - Legacy Backbone typing
    const handleBreadCrumbSetter = ({getCrumbs, setCrumbs}) => {
      const crumbs = getCrumbs()
      crumbs.push({name: I18n.t('People'), url: 'users'})
      crumbs.push({name: I18n.t('Groups'), url: ''})
      setCrumbs(crumbs)
    }
    initializeTopNavPortalWithDefaults({
      getBreadCrumbSetter: handleBreadCrumbSetter,
    })
  }

  // @ts-expect-error - Legacy Backbone typing
  getUrlEndString(url) {
    return url.slice(url.lastIndexOf('/') + 1)
  }

  // @ts-expect-error - Legacy Backbone typing
  render() {
    // @ts-expect-error - Legacy Backbone typing
    super.render(...arguments)
    if (this.collection.length > 1) {
      this.reorder()
    }
    this.refreshTabs()
    return this.loadTabFromUrl()
  }

  refreshTabs() {
    if (this.collection.length > 0) {
      // @ts-expect-error - Backbone View property
      this.$tabs.find('ul.ui-tabs-nav li.static').remove()
      // @ts-expect-error - Backbone View property
      this.$tabs.find('ul.ui-tabs-nav').prepend(this.$static)
    }
    // setup the tabs
    // @ts-expect-error - Backbone View property
    if (this.$tabs.data('ui-tabs')) {
      // @ts-expect-error - Backbone View property
      this.$tabs.tabs('refresh').show()
    } else {
      // @ts-expect-error - Backbone View property
      this.$tabs.tabs({cookie: {}}).show()
    }

    requestAnimationFrame(() => {
      // @ts-expect-error - Backbone View property
      const activeTab = this.$tabs.find('li.ui-tabs-active')
      activeTab.trigger('focus')
    })

    // @ts-expect-error - Backbone View property
    this.$tabs.tabs({
      // @ts-expect-error - Legacy Backbone typing
      beforeActivate(event, ui) {
        ui.newTab.trigger('focus')
        return !ui.newTab.hasClass('static')
      },
      // @ts-expect-error - Legacy Backbone typing
      activate: (event, ui) => {
        requestAnimationFrame(() => {
          ui.oldTab.removeClass('ui-state-focus')
          const activeItemHref = ui.newTab.find('a').attr('href')
          window.history.replaceState({}, document.title, activeItemHref)
          ui.newTab.trigger('focus')
        })
      },
    })

    // hide/show the instruction text
    if (this.collection.length > 0) {
      // @ts-expect-error - Backbone View property
      this.$emptyInstructions.hide()
    } else {
      // @ts-expect-error - Backbone View property
      this.$emptyInstructions.show()
      // hide the emtpy tab set which may have borders that would otherwise show
      // @ts-expect-error - Backbone View property
      this.$tabs.hide()
    }
    // @ts-expect-error - Backbone View property
    this.$tabs.find('li.static a').unbind()

    // Check if we came from internal navigation (groups or users page)
    const referrerEnd = this.getUrlEndString(document.referrer)
    const isInternalNavigation =
      document.referrer && new URL(document.referrer).origin === window.location.origin
    const needsVoiceOverDelay =
      (referrerEnd === 'groups' || referrerEnd === 'users') && isInternalNavigation

    // Set up W3C ARIA-compliant keyboard navigation for tabs
    // Arrow keys navigate AND load pages automatically
    // @ts-expect-error - Backbone View property
    setupTabKeyboardNavigation(this.$tabs, {
      autoActivate: true,
      handleHashNavigation: true,
      // @ts-expect-error - Legacy Backbone typing
      useVoiceOverDelay: needsVoiceOverDelay,
    })
  }

  loadTabFromUrl() {
    // @ts-expect-error - Backbone View property
    if (window.location.hash === '#new' && !this.pendingCreation) {
      // @ts-expect-error - Legacy Backbone typing
      return this.addGroupSet()
    } else {
      // @ts-expect-error - Backbone View property
      this.pendingCreation = false
      const id = window.location.hash.split('-')[1]
      if (id != null) {
        const model = this.collection.get(id)
        if (model) {
          // @ts-expect-error - Backbone View property
          return this.$tabs.tabs({active: this.tabOffsetOfModel(model)})
        }
      }
    }
  }

  // @ts-expect-error - Legacy Backbone typing
  tabOffsetOfModel(model) {
    const index = this.collection.indexOf(model)
    // @ts-expect-error - Backbone View property
    const numStatic = this.$static.length
    return index + numStatic
  }

  // @ts-expect-error - Legacy Backbone typing
  createItemView(model) {
    // create and add tab panel
    const panelId = `tab-${model.id != null ? model.id : model.cid}`
    const $panel = $('<div/>')
      .addClass('tab-panel')
      .attr('id', panelId)
      .data('loaded', false)
      .data('model', model)
    // @ts-expect-error - Backbone View property
    this.$tabs.append($panel)
    // If this is the first panel, load the contents
    // @ts-expect-error - Backbone View property
    if (this.$tabs.find('.tab-panel').length === 1) {
      // @ts-expect-error - Legacy Backbone typing
      this.loadPanelView($panel, model)
    }
    // create the <li> tab view
    const view = super.createItemView(...arguments)
    view.listenTo(model, 'change', () => {
      // e.g. change name
      view.render()
      this.reorder()
      return this.refreshTabs()
    })
    return view
  }

  // @ts-expect-error - Legacy Backbone typing
  renderItem() {
    // @ts-expect-error - Legacy Backbone typing
    super.renderItem(...arguments)
    return this.refreshTabs()
  }

  // @ts-expect-error - Legacy Backbone typing
  removeItem(model) {
    // @ts-expect-error - Legacy Backbone typing
    super.removeItem(...arguments)
    // remove the linked panel and refresh the tabs
    model.itemView.remove()
    if (model.panelView != null) {
      model.panelView.remove()
    }
    return this.refreshTabs()
  }

  // @ts-expect-error - Legacy Backbone typing
  async addGroupSet(e) {
    e?.preventDefault?.()
    // @ts-expect-error - Backbone View property
    this.pendingCreation = true
    const mountPoint = await awaitElement('create-group-set-modal-mountpoint')
    const createResult = await renderCreateDialog(mountPoint)
    if (createResult) {
      const cat = new GroupCategory()
      // @ts-expect-error - Backbone View property
      cat.set(createResult)
      this.collection.add(cat)
      // @ts-expect-error - Backbone View property
      window.location.hash = `tab-${createResult.id}`
      this.reorder()
      this.refreshTabs()
      // @ts-expect-error - Backbone View property
      this.$tabs.tabs({active: this.tabOffsetOfModel(cat)})
    }
  }

  // @ts-expect-error - Legacy Backbone typing
  activatedTab(event, ui) {
    const $panel = ui.newPanel
    return this.loadPanelView($panel)
  }

  // @ts-expect-error - Legacy Backbone typing
  loadPanelView($panel) {
    // there is a bug here where we load the first tab, then immediately load the tab from the hash
    if (!$panel.data('loaded')) {
      const model = $panel.data('model')
      // @ts-expect-error - Legacy Backbone typing
      const categoryView = new GroupCategoryView({model})
      // @ts-expect-error - Backbone View property
      categoryView.setElement($panel)
      // @ts-expect-error - Backbone View property
      categoryView.render()
      // return the created tab <li> view
      model.panelView = $panel
      // store now loaded
      $panel.data('loaded', true)
    }
    return $panel
  }

  toJSON() {
    // @ts-expect-error - Legacy Backbone typing
    const json = super.toJSON(...arguments)
    json.ENV = ENV
    const context = ENV.context_asset_string.split('_')
    json.context = context[0]
    json.isCourse = json.context === 'course'
    json.context_id = context[1]
    return json
  }
}
GroupCategoriesView.initClass()
